import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI, locationsAPI } from '../../api/bookings';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, Shield } from 'lucide-react';

export default function PricingManagement() {
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [pricingRules, setPricingRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  // New/Edit rule form
  const [dayType, setDayType] = useState('weekday');
  const [timeStart, setTimeStart] = useState('09:00');
  const [timeEnd, setTimeEnd] = useState('10:00');
  const [sport, setSport] = useState('both');
  const [pricePerHour, setPricePerHour] = useState('30');
  const [priority, setPriority] = useState('100');

  // Memoize the two filter passes that ran inline in JSX (one for the
  // grid, one for the empty-state count check, plus a 3rd for the default
  // section). Recomputing on every render is wasteful for arrays that
  // routinely hit 50+ rules per location.
  const nonDefaultRules = useMemo(
    () => pricingRules.filter((r) => !r.is_default),
    [pricingRules],
  );
  const defaultRules = useMemo(
    () => pricingRules.filter((r) => r.is_default),
    [pricingRules],
  );

  useEffect(() => {
    loadLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      loadPricingRules();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation]);

  const loadLocations = async () => {
    try {
      const locs = await locationsAPI.getLocations();
      setLocations(locs);
      if (locs.length > 0) {
        setSelectedLocation(locs[0]);
      }
    } catch (error) {
      toast.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const loadPricingRules = async () => {
    if (!selectedLocation) return;
    try {
      const rules = await adminAPI.getPricingRules(selectedLocation.id);
      // Sort by priority (lower number = higher priority, but show default rule last)
      const sortedRules = rules.sort((a, b) => {
        // Default rules go to the end
        if (a.is_default && !b.is_default) return 1;
        if (!a.is_default && b.is_default) return -1;
        // Otherwise sort by priority
        return (a.priority || 100) - (b.priority || 100);
      });
      setPricingRules(sortedRules);
    } catch (error) {
      toast.error('Failed to load pricing rules');
    }
  };

  const handleCreateRule = async () => {
    if (!selectedLocation) return;
    try {
      const rule = {
        location_id: selectedLocation.id,
        day_type: dayType,
        time_start: timeStart,
        time_end: timeEnd,
        sport: sport,
        price_per_hour: parseFloat(pricePerHour),
        priority: parseInt(priority) || 100,
        is_default: false
      };
      
      if (editingRule) {
        // Update existing rule - preserve is_default status
        rule.is_default = editingRule.is_default || false;
        if (editingRule.is_default) {
          rule.day_type = 'all';
          rule.priority = 999;
        }
        await adminAPI.updatePricingRule(editingRule.id, rule);
        toast.success('Pricing rule updated');
      } else {
        // Create new rule
        await adminAPI.createPricingRule(rule);
        toast.success('Pricing rule created');
      }
      
      setIsDialogOpen(false);
      resetForm();
      loadPricingRules();
    } catch (error) {
      toast.error('Failed to save pricing rule');
    }
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setDayType(rule.is_default ? 'all' : rule.day_type);
    setTimeStart(rule.time_start);
    setTimeEnd(rule.time_end);
    setSport(rule.sport);
    setPricePerHour(rule.price_per_hour.toString());
    setPriority((rule.priority || 100).toString());
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingRule(null);
    setDayType('weekday');
    setTimeStart('09:00');
    setTimeEnd('10:00');
    setSport('both');
    setPricePerHour('30');
    setPriority('100');
  };

  const handleDialogClose = (open) => {
    setIsDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  const handleDeleteRule = async (ruleId, isDefault) => {
    if (isDefault) {
      toast.error('Cannot delete the default pricing rule. You can only edit it.');
      return;
    }
    if (!window.confirm('Delete this pricing rule?')) return;
    try {
      await adminAPI.deletePricingRule(ruleId);
      toast.success('Pricing rule deleted');
      loadPricingRules();
    } catch (error) {
      toast.error('Failed to delete pricing rule');
    }
  };

  const formatDayType = (dayType, isDefault) => {
    if (isDefault || dayType === 'all') return 'Default (All Days)';
    return dayType.charAt(0).toUpperCase() + dayType.slice(1);
  };

  return (
    <div data-testid="pricing-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-black font-heading uppercase">Pricing Management</h1>
          {selectedLocation && (
            <p className="text-lg text-muted-foreground mt-2">
              Location: <span className="font-semibold text-primary">{selectedLocation.name}</span>
            </p>
          )}
        </div>
        <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
          <DialogTrigger asChild>
            <Button data-testid="add-pricing-rule-button" className="btn-volt" onClick={() => resetForm()}>
              <Plus className="w-5 h-5 mr-2" />
              Add Rule
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="add-rule-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase">
                {editingRule ? (editingRule.is_default ? 'Edit Default Pricing' : 'Edit Pricing Rule') : 'Add Pricing Rule'}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Configure price-per-hour rule by day type, time range, sport and priority.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Only show day type for non-default rules */}
              {!(editingRule?.is_default) && (
                <div>
                  <Label className="uppercase text-xs font-bold text-muted-foreground mb-2 block">Day Type</Label>
                  <Select value={dayType} onValueChange={setDayType}>
                    <SelectTrigger data-testid="day-type-select" className="h-12 rounded-2xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekday">Weekday</SelectItem>
                      <SelectItem value="weekend">Weekend</SelectItem>
                      <SelectItem value="holiday">Holiday</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {editingRule?.is_default && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                  <p className="text-sm text-yellow-600 dark:text-yellow-400">
                    <Shield className="w-4 h-4 inline mr-2" />
                    This is the default pricing rule. It applies when no other specific rules match.
                  </p>
                </div>
              )}
              
              {/* Only show time range for non-default rules */}
              {!(editingRule?.is_default) && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="time-start" className="uppercase text-xs font-bold text-muted-foreground mb-2 block">Start Time</Label>
                    <Input
                      id="time-start"
                      data-testid="time-start-input"
                      type="time"
                      value={timeStart}
                      onChange={(e) => setTimeStart(e.target.value)}
                      className="h-12 rounded-2xl"
                    />
                  </div>
                  <div>
                    <Label htmlFor="time-end" className="uppercase text-xs font-bold text-muted-foreground mb-2 block">End Time</Label>
                    <Input
                      id="time-end"
                      data-testid="time-end-input"
                      type="time"
                      value={timeEnd}
                      onChange={(e) => setTimeEnd(e.target.value)}
                      className="h-12 rounded-2xl"
                    />
                  </div>
                </div>
              )}
              
              {/* Only show sport selection for non-default rules */}
              {!(editingRule?.is_default) && (
                <div>
                  <Label className="uppercase text-xs font-bold text-muted-foreground mb-2 block">Sport</Label>
                  <Select value={sport} onValueChange={setSport}>
                    <SelectTrigger data-testid="sport-select" className="h-12 rounded-2xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="both">Both</SelectItem>
                      <SelectItem value="badminton">Badminton</SelectItem>
                      <SelectItem value="pickleball">Pickleball</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div>
                <Label htmlFor="price" className="uppercase text-xs font-bold text-muted-foreground mb-2 block">Price per Hour ($)</Label>
                <Input
                  id="price"
                  data-testid="price-input"
                  type="number"
                  step="0.01"
                  value={pricePerHour}
                  onChange={(e) => setPricePerHour(e.target.value)}
                  className="h-12 rounded-2xl"
                />
              </div>
              
              {/* Only show priority for non-default rules */}
              {!(editingRule?.is_default) && (
                <div>
                  <Label htmlFor="priority" className="uppercase text-xs font-bold text-muted-foreground mb-2 block">
                    Priority (Lower = Higher Priority)
                  </Label>
                  <Input
                    id="priority"
                    data-testid="priority-input"
                    type="number"
                    min="1"
                    max="998"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    className="h-12 rounded-2xl"
                    placeholder="100"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Rules with lower priority numbers are matched first (1-998)
                  </p>
                </div>
              )}
              
              <Button
                onClick={handleCreateRule}
                data-testid="create-rule-button"
                className="w-full btn-volt"
              >
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Location Selector */}
      {locations.length > 1 && (
        <div className="mb-6">
          <Label className="uppercase text-xs font-bold text-muted-foreground mb-2 block">Location</Label>
          <Select
            value={selectedLocation?.id}
            onValueChange={(val) => setSelectedLocation(locations.find(l => l.id === val))}
          >
            <SelectTrigger data-testid="location-select" className="h-12 rounded-2xl max-w-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {locations.map(loc => (
                <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Loading...</div>
      ) : pricingRules.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">No pricing rules configured</p>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Regular pricing rules */}
          <div>
            <h2 className="text-xl font-bold mb-4 font-heading">Pricing Rules</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {nonDefaultRules.map((rule) => (
                <Card key={rule.id} className="p-6" data-testid="pricing-rule-card">
                  {/* Location Badge */}
                  <div className="mb-3">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                      📍 {selectedLocation?.name || 'Unknown Location'}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="font-bold text-2xl">${rule.price_per_hour}/hr</p>
                      <p className="text-sm text-muted-foreground capitalize">{formatDayType(rule.day_type, rule.is_default)}</p>
                      {rule.priority && rule.priority !== 100 && (
                        <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                          Priority: {rule.priority}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEditRule(rule)}
                        data-testid={`edit-rule-${rule.id}`}
                      >
                        <Edit className="w-5 h-5 text-primary" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteRule(rule.id, rule.is_default)}
                        data-testid={`delete-rule-${rule.id}`}
                      >
                        <Trash2 className="w-5 h-5 text-destructive" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-bold">Time:</span> {rule.time_start} - {rule.time_end}</p>
                    <p><span className="font-bold">Sport:</span> <span className="capitalize">{rule.sport}</span></p>
                  </div>
                </Card>
              ))}
            </div>
            {nonDefaultRules.length === 0 && (
              <Card className="p-6 text-center text-muted-foreground">
                <p>No specific pricing rules. The default pricing will apply to all bookings.</p>
              </Card>
            )}
          </div>

          {/* Default pricing rule */}
          {defaultRules.map((rule) => (
            <div key={rule.id}>
              <h2 className="text-xl font-bold mb-4 font-heading flex items-center gap-2">
                <Shield className="w-5 h-5 text-yellow-500" />
                Default Pricing (Fallback)
              </h2>
              <Card className="p-6 border-yellow-500/30 bg-yellow-500/5" data-testid="default-pricing-card">
                {/* Location Badge */}
                <div className="mb-3">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                    📍 {selectedLocation?.name || 'Unknown Location'}
                  </span>
                </div>
                
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="font-bold text-3xl text-yellow-600 dark:text-yellow-400">${rule.price_per_hour}/hr</p>
                    <p className="text-sm text-muted-foreground">
                      Applies when no other rules match
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditRule(rule)}
                    data-testid={`edit-default-rule-${rule.id}`}
                    className="border-yellow-500/50 text-yellow-600 hover:bg-yellow-500/10"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Default Price
                  </Button>
                </div>
                <div className="text-sm text-muted-foreground">
                  <p>• All days (Weekday, Weekend, Holiday)</p>
                  <p>• All time slots (00:00 - 23:59)</p>
                  <p>• Both sports (Badminton & Pickleball)</p>
                </div>
              </Card>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
